import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";

/**
 * Subtle "live activity" ticker for the landing-page pro spotlight: a pulsing
 * live dot + a rotating recent-hire line + a "N hired today" counter that ticks
 * up occasionally, for urgency and social proof. Data is synthesized marketing
 * data from GET /api/activity/live (public). Renders nothing if the fetch fails.
 */
export default function LiveActivityTicker() {
    const [events, setEvents] = useState([]);
    const [hired, setHired] = useState(null);
    const [idx, setIdx] = useState(0);
    const [bumped, setBumped] = useState(false);
    const cap = useRef(Infinity);

    useEffect(() => {
        let alive = true;
        api.get("/activity/live")
            .then((r) => {
                if (!alive) return;
                setEvents(r.data?.events || []);
                setHired(r.data?.hired_today ?? null);
                cap.current = (r.data?.hired_today ?? 0) + 7;
            })
            .catch(() => { if (alive) setEvents([]); });
        return () => { alive = false; };
    }, []);

    // Rotate the visible event line.
    useEffect(() => {
        if (events.length < 2) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % events.length), 3600);
        return () => clearInterval(t);
    }, [events.length]);

    // Occasionally tick the "hired today" count up so it feels live.
    useEffect(() => {
        if (hired == null) return;
        const t = setInterval(() => {
            setHired((h) => {
                if (h == null || h >= cap.current) return h;
                setBumped(true);
                setTimeout(() => setBumped(false), 900);
                return h + 1;
            });
        }, 24000);
        return () => clearInterval(t);
    }, [hired]);

    if (!events.length || hired == null) return null;

    const ev = events[idx];

    return (
        <div
            data-testid="live-activity-ticker"
            className="mb-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-zinc-950/50 px-4 py-2.5"
        >
            <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>

            <div className="relative min-w-0 flex-1 overflow-hidden">
                <AnimatePresence mode="wait">
                    <motion.p
                        key={idx}
                        data-testid="live-activity-text"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="truncate text-xs sm:text-sm text-zinc-300 dark:text-zinc-300"
                    >
                        <span className="font-semibold text-white dark:text-white">{ev.who}</span>
                        {ev.text.replace(ev.who, "")}
                        <span className="ml-1 text-zinc-500">· {ev.mins_ago} min ago</span>
                    </motion.p>
                </AnimatePresence>
            </div>

            <span
                data-testid="live-hired-count"
                className={`shrink-0 whitespace-nowrap text-xs font-semibold transition-transform duration-300 ${bumped ? "scale-110" : "scale-100"}`}
            >
                <span className="text-orange-500">{hired}</span>
                <span className="hidden sm:inline text-zinc-500 dark:text-zinc-500"> hired today</span>
                <span className="sm:hidden text-zinc-500 dark:text-zinc-500"> today</span>
            </span>
        </div>
    );
}
